import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, NavLink, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { LogOut, LayoutDashboard, Users, Package, FileText, Settings, MapPin, Building, FolderOpen } from 'lucide-react';

const AdminLayout = () => {
  const [admin, setAdmin] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const currentAdmin = authService.getCurrentAdmin();
    if (!currentAdmin) {
      navigate('/admin');
    } else {
      setAdmin(currentAdmin);
    }
  }, [navigate]);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/contacts', icon: Users, label: 'Contacts' },
    { path: '/admin/lots', icon: MapPin, label: 'Lots' },
    { path: '/admin/warehouses', icon: Package, label: 'Warehouses' },
    { path: '/admin/build-to-suit', icon: Building, label: 'Build-to-Suit' },
    { path: '/admin/documents', icon: FolderOpen, label: 'Documents' },
    { path: '/admin/content', icon: FileText, label: 'Content' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">HEXAPARKS</h1>
              <span className="ml-3 px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                Admin
              </span>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-sm">
                <p className="text-gray-900 font-medium">{admin?.admin?.name || 'Admin'}</p>
                <p className="text-gray-500 text-xs">{admin?.admin?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-red-600 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white min-h-[calc(100vh-64px)] border-r border-gray-200">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-amber-50 text-amber-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
